import { Link2, CheckCircle, Key, Wallet } from "lucide-react";
import type { OrgaConfiguration } from "@/models/orgaConfiguration";

export type StripeStatus = "pending" | "active" | "suspended" | "rejected";

// Root-level PaymentMode - inclut tous les futurs providers
export type PaymentMode = "stripe_connect" | "stripe_api_key" | "sumup" | "paypal" | "stancer" | "klarna" | "none";

// ============================================================================
// LEGACY STRIPE CONFIG (pour compatibilité avec anciennes données)
// ============================================================================

export interface StripeConfig {
    // Mode de paiement (deprecated - use root-level paymentMode instead)
    paymentMode?: "stripe_connect" | "stripe_api_key";

    // Stripe Connect
    stripeAccountId?: string;
    stripeStatus: StripeStatus;
    stripeVerified: boolean;
    stripeConnectUrl?: string;
    stripeDashboardUrl?: string;
    chargesEnabled?: boolean;
    payoutsEnabled?: boolean;
    detailsSubmitted?: boolean;

    // Stripe API Key (legacy - valeurs directes)
    stripeSecretKey?: string;
    stripePublishableKey?: string;
    stripeApiKeyWebhookSecret?: string;

    // Nouveaux champs (métadonnées - pas les vraies clés)
    hasSecretKey?: boolean;
    secretKeyMasked?: string;
    hasPublishableKey?: boolean;
    publishableKeyMasked?: string;
    hasWebhookSecret?: boolean;
    webhookSecretMasked?: string;
    accountName?: string;
    isTestMode?: boolean;

    // Configuration des commissions
    commissionRate?: number;
    fixedCommission?: number;
    autoTransferEnabled: boolean;
    transferDelay: number;

    // Informations business
    businessProfile?: {
        name?: string;
        url?: string;
    };
}

// ============================================================================
// PAYMENT PROVIDER INTERFACE
// ============================================================================

export interface PaymentProvider {
    id: string;
    title: string;
    icon: React.ElementType;
    description: string;
    isRecommended?: boolean;
    /**
     * Vérifie si le provider est configuré
     * Supporte à la fois la nouvelle architecture (stripeConnectConfig/stripeApiKeyConfig)
     * et l'ancienne (stripeConfig) pour compatibilité
     */
    configCheck: (config?: StripeConfig, orgConfig?: OrgaConfiguration) => boolean;
    features: {
        icon: React.ElementType;
        text: string;
        color: 'success' | 'warning';
        iconBg?: 'success' | 'warning';
    }[];
    details?: string[];
    fees: {
        label: string;
        value: string;
        badge?: boolean;
        badgeColor?: 'success' | 'warning';
    }[];
}

// ============================================================================
// HELPER FUNCTIONS - Vérification de configuration multi-provider
// ============================================================================

/**
 * Vérifie si un compte Stripe Connect a été créé ou est en cours de configuration.
 * Supporte la nouvelle architecture ET l'ancienne.
 */
export function hasStripeConnectAccount(orgConfig?: OrgaConfiguration): boolean {
    if (!orgConfig) return false;

    // Nouvelle architecture
    if (orgConfig.stripeConnectConfig) {
        const hasAccount = Boolean(orgConfig.stripeConnectConfig.accountId);
        if (hasAccount) return true;
    }

    // Legacy
    const legacy = orgConfig.stripeConfig;
    if (legacy?.stripeAccountId) {
        return true;
    }

    return false;
}

/**
 * Vérifie si Stripe Connect est configuré
 * Supporte la nouvelle architecture ET l'ancienne
 */
export function isStripeConnectConfigured(orgConfig?: OrgaConfiguration): boolean {
    if (!orgConfig) return false;

    // Nouvelle architecture
    if (orgConfig.stripeConnectConfig) {
        const sc = orgConfig.stripeConnectConfig;
        return Boolean(sc.accountId && (
            sc.status === 'active' ||
            (sc.chargesEnabled && sc.payoutsEnabled && sc.detailsSubmitted)
        ));
    }

    // Legacy
    const legacy = orgConfig.stripeConfig;
    if (legacy?.stripeAccountId) {
        return Boolean(
            legacy.stripeStatus === 'active' ||
            (legacy.chargesEnabled && legacy.payoutsEnabled && legacy.detailsSubmitted)
        );
    }

    return false;
}

/**
 * Vérifie si Stripe API Key est configuré
 * Supporte la nouvelle architecture ET l'ancienne
 */
export function isStripeApiKeyConfigured(orgConfig?: OrgaConfiguration): boolean {
    if (!orgConfig) return false;

    // Nouvelle architecture (Secret Manager)
    if (orgConfig.stripeApiKeyConfig) {
        return orgConfig.stripeApiKeyConfig.hasSecretKey === true;
    }

    // Legacy (clés dans Firestore)
    const legacy = orgConfig.stripeConfig;
    if (legacy) {
        // Vérifier les nouveaux champs de métadonnées
        if (legacy.hasSecretKey) return true;
        // Fallback sur la présence de la vraie clé (anciennes configs)
        if (legacy.stripeSecretKey && legacy.stripeSecretKey.length > 0) return true;
    }

    return false;
}



// ============================================================================
// PAYMENT PROVIDERS CONFIGURATION
// ============================================================================

export const PAYMENT_PROVIDERS: PaymentProvider[] = [
    {
        id: 'stripe_connect',
        title: 'Stripe Connect',
        icon: Link2,
        description: 'Pas de compte Stripe requis • Inscription rapide',
        isRecommended: true,
        configCheck: (_config, orgConfig) => isStripeConnectConfigured(orgConfig),
        features: [
            { icon: CheckCircle, text: 'Aucun compte Stripe existant requis', color: 'success' },
            { icon: CheckCircle, text: 'Tout est automatisé pour vous', color: 'success' }
        ],
        details: [
            'Créez un compte Stripe intégré à CashLive en quelques clics',
            'Frais bancaires Stripe : ~1.5% + 0,25€ par transaction',
            'Commissions et frais de port prélevés automatiquement',
            'Virement vers votre compte bancaire : sous 3 jours'
        ],
        fees: [
            { label: 'Frais Stripe :', value: '~1.5% + 0,25€' },
            { label: 'Commission CashLive :', value: 'Prélèv. Auto', badge: true, badgeColor: 'success' }
        ]
    },
    {
        id: 'stripe_api_key',
        title: 'Mon compte Stripe',
        icon: Key,
        description: 'Connectez votre propre compte Stripe',
        configCheck: (_config, orgConfig) => isStripeApiKeyConfigured(orgConfig),
        features: [
            { icon: CheckCircle, text: 'Contrôle total des fonds', color: 'success' },
            { icon: CheckCircle, text: 'Frais de port à gérer manuellement', color: 'warning', iconBg: 'warning' }
        ],
        details: [
            'Frais bancaires Stripe : ~1.5% + 0,25€ par transaction',
            'Commissions CashLive : Facturées mensuellement par prélèvement',
            'Frais de port : Paiement groupé, à l\'unité ou via crédits prépayés',
            'Virement vers votre compte (Payout) : 2-7 jours selon votre Stripe'
        ],
        fees: [
            { label: 'Frais Stripe :', value: '~1.5% + 0,25€' },
            { label: 'Commission CashLive :', value: 'Fact. Mensuelle', badge: true, badgeColor: 'warning' }
        ]
    },
    {
        id: 'paypal',
        title: 'PayPal',
        icon: Wallet,
        description: 'Connectez votre propre compte PayPal Business',
        configCheck: (_config, orgConfig) => Boolean(orgConfig?.paypalConfig?.hasClientSecret),
        features: [
            { icon: CheckCircle, text: 'Paiement via compte PayPal ou CB', color: 'success' },
            { icon: CheckCircle, text: 'Frais de port à gérer manuellement', color: 'warning', iconBg: 'warning' }
        ],
        details: [
            'Frais PayPal : ~2.9% + 0,35€ par transaction',
            'Commissions CashLive : Facturées mensuellement par prélèvement',
            'Frais de port : Paiement groupé, à l\'unité ou via crédits prépayés',
            'Virement vers votre compte : Immédiat sur votre solde PayPal'
        ],
        fees: [
            { label: 'Frais PayPal :', value: '~2.9% + 0,35€' },
            { label: 'Commission CashLive :', value: 'Fact. Mensuelle', badge: true, badgeColor: 'warning' }
        ]
    }
];

/**
 * Récupère un provider par son ID
 */
export function getPaymentProviderById(id: string): PaymentProvider | undefined {
    return PAYMENT_PROVIDERS.find(p => p.id === id);
}

/**
 * Vérifie si un provider est configuré pour une organisation
 */
export function isProviderConfigured(providerId: string, orgConfig?: OrgaConfiguration): boolean {
    const provider = getPaymentProviderById(providerId);
    if (!provider) return false;
    return provider.configCheck(orgConfig?.stripeConfig, orgConfig);
}
